import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next'; // Import useTranslation
import {
  collection,
  query,
  where,
  getCountFromServer,
  getDocs,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
// Session type might not be strictly needed here if only accessing price and status
// import { Session } from '../types/firestoreTypes';

// Interface for the hook's return value
interface MonthlyIncome {
  [currency: string]: number; // e.g., { "EUR": 1500, "HUF": 500000 }
}

interface DashboardStats {
  activePatientCount: number;
  monthlyIncome: MonthlyIncome; // Realized income (Completed/Invoiced)
  predictedMonthlyIncome: MonthlyIncome; // Predicted income (Scheduled/Completed/Invoiced)
  isLoading: boolean;
  error: string | null; // Will hold translated error string
}

export const useDashboardStats = (): DashboardStats => {
  const { t } = useTranslation(); // Get the t function
  const { user } = useAuth();
  const [activePatientCount, setActivePatientCount] = useState(0);
  const [monthlyIncome, setMonthlyIncome] = useState<MonthlyIncome>({});
  const [predictedMonthlyIncome, setPredictedMonthlyIncome] =
    useState<MonthlyIncome>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null); // State holds translated error

  // Function to fetch all necessary stats
  const fetchStats = useCallback(async () => {
    // Ensure user is logged in
    if (!user) {
      console.log('useDashboardStats: No user, skipping fetch.');
      setIsLoading(false);
      // Optionally set an error or return early if user is required
      // setError(t('hooks.useDashboardStats.errors.notAuthenticated'));
      return;
    }

    console.log('useDashboardStats: Fetching dashboard stats...');
    setIsLoading(true);
    setError(null); // Clear previous errors

    try {
      // --- Calculate current month date range ---
      const now = new Date();
      // Start of the current month (e.g., May 1st, 00:00:00)
      const startOfMonth = new Date(
        now.getFullYear(),
        now.getMonth(),
        1,
        0,
        0,
        0
      );
      // Start of the *next* month (e.g., June 1st, 00:00:00)
      const startOfNextMonth = new Date(
        now.getFullYear(),
        now.getMonth() + 1,
        1,
        0,
        0,
        0
      );

      // --- Query for active patients count ---
      const activePatientQuery = query(
        collection(db, 'patients'),
        where('therapistId', '==', user.uid),
        where('status', '==', 'Active') // Assuming 'Active' is the status string
      );

      // --- Query for this month's sessions (relevant for income calculation) ---
      const monthlySessionsQuery = query(
        collection(db, 'sessions'),
        where('therapistId', '==', user.uid),
        // Include statuses that contribute to predicted income
        where('status', 'in', ['Scheduled', 'Completed', 'Invoiced']),
        // Filter by date range for the current month
        where('sessionDateTime', '>=', Timestamp.fromDate(startOfMonth)),
        where('sessionDateTime', '<', Timestamp.fromDate(startOfNextMonth)) // Use '<' next month start
      );

      // --- Execute queries concurrently ---
      const [patientSnapshot, sessionsSnapshot] = await Promise.all([
        getCountFromServer(activePatientQuery), // Use getCountFromServer for efficiency
        getDocs(monthlySessionsQuery),
      ]);

      // --- Process results ---
      // Active patient count
      const count = patientSnapshot.data().count;
      setActivePatientCount(count);
      console.log(`Active patient count: ${count}`);

      // Monthly income calculation
      let realizedIncomeSummary: MonthlyIncome = {};
      let predictedIncomeSummary: MonthlyIncome = {};

      sessionsSnapshot.docs.forEach((doc) => {
        const session = doc.data();
        // Check if price data exists and is valid
        if (
          session.price &&
          typeof session.price.amount === 'number' &&
          session.price.currency
        ) {
          const { amount, currency } = session.price;

          // Initialize currency buckets if they don't exist
          predictedIncomeSummary[currency] =
            predictedIncomeSummary[currency] || 0;
          realizedIncomeSummary[currency] =
            realizedIncomeSummary[currency] || 0;

          // Add to predicted income (all fetched sessions: Scheduled, Completed, Invoiced)
          // Handle HUF rounding specifically
          const valueToAdd = currency === 'HUF' ? Math.round(amount) : amount;
          predictedIncomeSummary[currency] += valueToAdd;

          // Add to realized income (only Completed or Invoiced sessions)
          if (session.status === 'Completed' || session.status === 'Invoiced') {
            realizedIncomeSummary[currency] += valueToAdd;
          }
        } else {
          console.warn(`Session ${doc.id} missing valid price information.`);
        }
      });

      console.log('Realized Income:', realizedIncomeSummary);
      console.log('Predicted Income:', predictedIncomeSummary);
      setMonthlyIncome(realizedIncomeSummary);
      setPredictedMonthlyIncome(predictedIncomeSummary);
    } catch (err) {
      // Handle errors during fetch
      console.error('Error fetching dashboard stats:', err);
      setError(t('hooks.useDashboardStats.errors.loadFailed')); // Set translated error
    } finally {
      // Ensure loading state is reset
      setIsLoading(false);
    }
    // Add t to dependency array
  }, [user, t]);

  // Effect to run fetchStats on mount and when user changes
  useEffect(() => {
    fetchStats();
  }, [fetchStats]); // fetchStats dependency includes user and t

  // Return the state values
  return {
    activePatientCount,
    monthlyIncome,
    predictedMonthlyIncome,
    isLoading,
    error, // Return translated error string
  };
};
